import { DISPLAY_DATE_FORMAT } from 'constants/dates';
import { PRIVILEGES } from 'constants/privileges';
import {
  useState,
  useCallback,
  MouseEvent,
  ChangeEvent,
  useMemo,
  useEffect,
  Fragment,
} from 'react';
import { Divider, Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import { AlertService } from 'api';
import { Alert as AlertData } from 'api/alerts/alerts.types';
import {
  CustomTablePagination,
  StyledTableCell,
  StyledTableHeadCell,
} from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { format } from 'date-fns';
import { Invoice } from 'pages/invoices/invoices.types';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { InvoiceTab } from 'root/pathnames';
import { SortDirection } from 'types/invoices';
import { hasPrivilege } from 'utils/authFunctions.utils';
import { isInvoiceCorrective } from 'utils/invoiceFunctions.utils';
import { numberWithCommas } from 'utils/numberFunctions.utils';
import { Filters } from '../filters';
import { InvoicesTabs } from '../InvoicesTabs';
import { KsefStatusChip } from '../KsefStatusChip';
import { InvoiceTableActions } from './components/InvoicesTableActions';
import { InvoicesTableSummary } from './components/InvoicesTableSummary';
import { KsefActionsCell } from './components/KsefActionsCell';
import { SaldeoErrorsList } from './components/SaldeoErrorsList/SaldeoErrorsList';
import * as S from './InvoicesTable.css';
import {
  InvoicesTableProps,
  HeadCell,
  TableData,
  SortProperties,
} from './invoicesTable.types';
import {
  getInvoiceDisplayedStatus,
  getInvoiceKsefState,
  getMarkedInvoiceHint,
  getSortProperties,
} from './InvoicesTable.utils';
import {
  ownHeadCells,
  foreignHeadCells,
  internalHeadCells,
  administrativeHeadCells,
} from './invoiceTable.const';

function createTableData(invoice: Invoice, invoiceTab?: InvoiceTab): TableData {
  const isCorrective = isInvoiceCorrective(invoice.invoiceClass);

  return {
    code: invoice.code,
    jobCode: invoice.createdFrom?.code,
    client: invoice.buyerCounterparty.code,
    saleDate: invoice.saleDate
      ? format(invoice.saleDate, DISPLAY_DATE_FORMAT)
      : '',
    issueDate: invoice.issueDate
      ? format(invoice.issueDate, DISPLAY_DATE_FORMAT)
      : '',
    dueDate: invoice.dueDate
      ? format(invoice.dueDate, DISPLAY_DATE_FORMAT)
      : '',
    grossValue:
      numberWithCommas(invoice.totals.grossValue) +
      ' ' +
      invoice.currencyCode.currency,
    status: invoice.status,
    displayedStatus: getInvoiceDisplayedStatus(invoice, invoiceTab),
    sellerCounterpartyName: invoice.sellerCounterparty.code,
    logNumber: invoice.logNumber,
    id: invoice.id,
    currency: invoice.currencyCode.currency,
    isCorrective,
    paid: invoice.paid,
    deleted: invoice.deleted,
    containsComplainedAllocations:
      !isCorrective && invoice.containsComplainedAllocations,
    containsUnrecognizedInvoiceLines:
      !isCorrective &&
      invoice.invoiceLines.some(
        (line) => line.activity.discriminant === 'UNRECOGNIZED',
      ),
    containsFailedAllocations:
      !isCorrective &&
      invoice.invoiceLines.some((line) =>
        line.description?.includes('ALLOCATION_FAILED'),
      ),
    exportInformation: invoice.exportInformation,
    publicationDate: invoice.publicationDate,
    owner: invoice.owner,
    ksefState: getInvoiceKsefState(invoice, invoiceTab),
    ksefActions: {
      ksefNumber: invoice.ksefNumber,
      ksefQrLink: invoice.ksefQrLink,
    },
  };
}

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof TableData,
  ) => void;
  onSelectAllClick: (event: ChangeEvent<HTMLInputElement>) => void;
  order?: Order;
  orderBy?: string;
  sortProperties: SortProperties;
  rowCount: number;
  cells: HeadCell[];
}

function EnhancedTableHead({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  cells,
  sortProperties,
}: EnhancedTableProps) {
  const hasKsefActions = cells.some((cell) => cell.id === 'ksefActions');
  const createSortHandler =
    (property: keyof TableData) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <StyledTableHeadCell padding='checkbox'>
          <Checkbox
            color='primary'
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'Select all invoices',
            }}
          />
        </StyledTableHeadCell>
        {cells.map((headCell) => {
          if (headCell.id === 'ksefActions') return null;

          const sortProperty = sortProperties[headCell.id];
          const isSortActive =
            Boolean(sortProperty) && orderBy === sortProperty;
          return (
            <StyledTableHeadCell
              key={headCell.id}
              align={headCell.align}
              padding='normal'
              sortDirection={isSortActive ? order : false}
              colSpan={headCell.id === 'ksefState' && hasKsefActions ? 2 : 1}
            >
              {sortProperty ? (
                <TableSortLabel
                  active={isSortActive}
                  direction={order}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {isSortActive ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === SortDirection.DESC
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </StyledTableHeadCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export function InvoicesTable({
  data,
  params,
  setParams,
  invoicesLoading,
  onAction,
}: InvoicesTableProps) {
  const { user } = useAuth();
  const [selected, setSelected] = useState<TableData[]>([]);
  const [saldeoErrors, setSaldeoErrors] = useState<AlertData[]>([]);
  const { tab: invoiceTab } = useParams<{ tab: InvoiceTab }>();

  const navigate = useNavigate();
  const location = useLocation();

  const { sortProperty, sortDirection } = params;

  const sortProperties = getSortProperties(params.own);

  const rows = useMemo(
    () => data.invoices.map((invoice) => createTableData(invoice, invoiceTab)),
    [data, invoiceTab],
  );

  const handleRequestSort = (
    _: MouseEvent<unknown>,
    property: keyof TableData,
  ) => {
    const nextSortProperty = sortProperties[property];
    if (!nextSortProperty) return;
    const isAsc = sortDirection === SortDirection.ASC;
    setParams((prev) => ({
      ...prev,
      sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
      sortProperty: nextSortProperty,
    }));
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(rows.filter((r) => !r.deleted));
      return;
    }
    setSelected([]);
  };

  const handleChecked = (event: MouseEvent<unknown>, id: TableData) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected: TableData[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setParams((prev) => ({
      ...prev,
      resultSize: parseInt(event.target.value, 10),
      pageNumber: 1,
    }));
  };

  const isSelected = (row: TableData) => selected.indexOf(row) !== -1;

  const tableHeaderCells: HeadCell[] = useMemo(() => {
    if (invoiceTab === InvoiceTab.INTERNAL) return internalHeadCells;
    if (params.administrative) {
      return administrativeHeadCells;
    } else if (params.own) {
      return rows.some((row) => row.ksefState === 'ACCEPTED')
        ? ownHeadCells
        : ownHeadCells.filter((cell) => cell.id !== 'ksefActions');
    } else {
      return foreignHeadCells;
    }
  }, [params.own, params.administrative, invoiceTab, rows]);

  const fetchTabAlerts = useCallback(async () => {
    if (invoiceTab === InvoiceTab.INTERNAL) {
      setSaldeoErrors([]);
      return;
    }
    const keyword =
      invoiceTab === InvoiceTab.FOREIGN
        ? 'saldeo'
        : invoiceTab === InvoiceTab.OWN ||
          invoiceTab === InvoiceTab.ADMINISTRATIVE
        ? 'enova'
        : '';

    const res = await AlertService.getAlertsInbox();
    setSaldeoErrors(
      res.data.result.filter((a) => a.message.toLowerCase().includes(keyword)),
    );
  }, [invoiceTab]);

  const handleAction = async () => {
    await onAction();
    await fetchTabAlerts();
    setSelected([]);
  };

  const handleDismissSaldeoError = useCallback((id: number) => {
    setSaldeoErrors((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const handleRedirectToDetails = (row: TableData) => {
    hasPrivilege(PRIVILEGES.Invoices.VIEW_INVOICE_DETAILS, user) &&
      navigate(
        `${location.pathname}/${
          row.isCorrective ? 'correctiveinvoice' : 'invoice'
        }/${row.id}`,
      );
  };

  useEffect(() => {
    setSelected([]);
  }, [params]);

  useEffect(() => {
    fetchTabAlerts();
  }, [fetchTabAlerts]);

  return (
    <Box sx={{ width: '100%', maxWidth: '1524px' }}>
      <InvoiceTableActions
        selectedInvoices={selected}
        onAction={handleAction}
        onSaldeoErrors={setSaldeoErrors}
      />
      {invoiceTab !== InvoiceTab.INTERNAL && (
        <SaldeoErrorsList
          alerts={saldeoErrors}
          onDismiss={handleDismissSaldeoError}
        />
      )}
      <Paper sx={{ width: '100%', margin: '1rem 0' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            pl: { sm: 2 },
          }}
        >
          <InvoicesTabs />
        </Box>
        <Filters params={params} setParams={setParams} />
        <Divider />
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          count={data.count}
          rowsPerPage={params.resultSize}
          page={params.pageNumber - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <Divider />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby='tableTitle'
            size='medium'
          >
            <colgroup>
              <col />
              {tableHeaderCells.map((cell) => (
                <col
                  key={cell.id}
                  style={
                    cell.id === 'ksefActions' ? { width: '60px' } : undefined
                  }
                />
              ))}
            </colgroup>
            <EnhancedTableHead
              numSelected={selected.length}
              order={sortDirection}
              orderBy={sortProperty}
              sortProperties={sortProperties}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.filter((r) => !r.deleted).length}
              cells={tableHeaderCells}
            />
            <TableBody>
              {rows.map((row) => {
                const isItemSelected = isSelected(row);
                const labelId = `enhanced-table-checkbox-${row.code}`;
                return (
                  <Fragment key={row.id}>
                    <S.InvoicesTableRow
                      key={row.id}
                      hover
                      $deleted={row.deleted && !invoicesLoading}
                      $highlighted={
                        row.containsComplainedAllocations && !invoicesLoading
                      }
                      $marked={
                        (row.containsUnrecognizedInvoiceLines ||
                          row.containsFailedAllocations) &&
                        !invoicesLoading
                      }
                      title={getMarkedInvoiceHint(row)}
                      tabIndex={-1}
                      selected={isItemSelected}
                      sx={{ cursor: 'pointer', height: '48px' }}
                    >
                      <TableCell
                        padding={!invoicesLoading ? 'checkbox' : 'normal'}
                      >
                        {!invoicesLoading ? (
                          <Checkbox
                            disabled={row.deleted}
                            color='primary'
                            checked={isItemSelected}
                            onClick={(event) => handleChecked(event, row)}
                            inputProps={{
                              'aria-labelledby': labelId,
                            }}
                          />
                        ) : (
                          <Skeleton
                            variant='circular'
                            width='15px'
                            height='15px'
                          />
                        )}
                      </TableCell>
                      {tableHeaderCells.map((cell) => (
                        <StyledTableCell
                          key={cell.id}
                          align={cell.align || 'left'}
                          height='condensed'
                          style={
                            cell.id === 'ksefActions'
                              ? {
                                  width: 60,
                                  minWidth: 60,
                                  maxWidth: 60,
                                  paddingLeft: 2,
                                  paddingRight: 2,
                                }
                              : undefined
                          }
                          sx={
                            cell.id === 'client' ||
                            cell.id === 'sellerCounterpartyName'
                              ? {
                                  maxWidth: 225,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              : undefined
                          }
                          title={
                            cell.id === 'client'
                              ? row.client
                              : cell.id === 'sellerCounterpartyName'
                              ? row.sellerCounterpartyName
                              : undefined
                          }
                          $disabled={
                            !hasPrivilege(
                              PRIVILEGES.Invoices.VIEW_INVOICE_DETAILS,
                              user,
                            )
                          }
                          onClick={
                            cell.id === 'ksefActions'
                              ? undefined
                              : () => handleRedirectToDetails(row)
                          }
                        >
                          {invoicesLoading ? (
                            <Skeleton />
                          ) : cell.id === 'ksefState' ? (
                            <KsefStatusChip state={row.ksefState} />
                          ) : cell.id === 'ksefActions' ? (
                            <KsefActionsCell {...row.ksefActions} />
                          ) : (
                            row[cell.id]
                          )}
                        </StyledTableCell>
                      ))}
                    </S.InvoicesTableRow>
                  </Fragment>
                );
              })}
              <InvoicesTableSummary
                selectedInvoices={selected}
                headerCells={tableHeaderCells}
                numSelected={selected.length}
                onSelectAllClick={handleSelectAllClick}
                rowCount={rows.filter((r) => !r.deleted).length}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          count={data.count}
          rowsPerPage={params.resultSize}
          page={params.pageNumber - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <InvoiceTableActions
        selectedInvoices={selected}
        onAction={handleAction}
        onSaldeoErrors={setSaldeoErrors}
      />
    </Box>
  );
}
